import { useMemo, useCallback, useState } from "react";
import { Alert, Skeleton, Table, TableColumnsType, Tag } from "antd";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Client } from "../../bitmaker/schemas/Billing/Client";

import { useClients } from "../../hooks/useClients";

import ClientFormModal from "./ClientFormModal";

import { getClientDisplayTitle } from "../../utils/billing";

export default function ClientsList() {
  const navigate = useNavigate();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { clients, loadingClients, fetchClientsError, refetchClients } =
    useClients();

  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const clientTableColumns: TableColumnsType<Client> = useMemo(
    () => [
      {
        title: "CLIENT",
        dataIndex: "clientName",
        key: "clientName",
        width: "30%",
        render: (_: any, record: Client) => {
          const { primary, secondary } = getClientDisplayTitle(record);

          return (
            <div>
              <div className="font-semibold">{primary || "N/A"}</div>
              {secondary && primary !== secondary && (
                <div className="text-xs text-gray-500">{secondary}</div>
              )}
            </div>
          );
        },
      },
      {
        title: "CONTACT",
        dataIndex: "contact",
        key: "contact",
        width: "20%",
        render: (_: any, record: Client) => (
          <div>
            <div className="text-sm text-gray-700">
              {record.emailAddress || "No email provided"}
            </div>
            <div className="text-xs text-gray-500">
              {record.phoneNumber || "No phone number provided"}
            </div>
          </div>
        ),
      },
      {
        title: "LOCATION",
        dataIndex: "location",
        key: "location",
        width: "30%",
        render: (_: any, record: Client) => {
          const addressDetails = [
            record.addressLine,
            record.townCity,
            record.stateProvince,
          ]
            .filter(Boolean)
            .join(", ");
          const countryDetails = [record.country, record.postalCode]
            .filter(Boolean)
            .join(" ");
          const hasData =
            addressDetails.length > 0 || countryDetails.length > 0;

          return (
            <div>
              <div className="text-sm text-gray-700">
                {addressDetails || "No address details"}
              </div>

              <div className="text-xs text-gray-500">
                {countryDetails || (hasData ? "" : "N/A")}
              </div>
            </div>
          );
        },
      },
      {
        title: "TAX ID",
        dataIndex: "taxNumber",
        key: "taxNumber",
        width: "10%",
        render: (_: any, record: Client) => (
          <div className="text-sm text-gray-700">
            {record.taxNumber ? (
              <>
                <div className="font-medium">{record.taxNumber}</div>
                <div className="text-xs text-gray-500">
                  {record.taxName || "ID"}
                </div>
              </>
            ) : (
              <span className="text-gray-400">N/A</span>
            )}
          </div>
        ),
      },
      {
        title: "CONFIGURATION",
        dataIndex: "settings",
        key: "settings",
        width: "10%",
        align: "center" as const,
        render: (_: any, record: Client) => (
          <div className="flex flex-col gap-1">
            {record.currency && (
              <Tag color="blue" className="w-fit self-center">
                {record.currency.toUpperCase()}
              </Tag>
            )}
            {record.language && (
              <Tag color="default" className="w-fit self-center">
                {record.language.toUpperCase()}
              </Tag>
            )}
            {!record.currency && !record.language && (
              <span className="text-gray-500 text-sm">N/A</span>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  const isInitialLoading = loadingClients && clients.length === 0;

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Clients</h2>
        <button
          type="button"
          onClick={handleOpenCreateModal}
          className="btn btn-green"
          disabled={loadingClients}
        >
          <Plus size={16} />
          <span>Add Client</span>
        </button>
      </div>

      {/* Error */}
      {fetchClientsError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load clients"
            description="Something went wrong while fetching data. Please try again."
            action={
              <button
                type="button"
                className="btn btn-gray"
                onClick={refetchClients}
                disabled={loadingClients}
              >
                Retry
              </button>
            }
          />
        </div>
      )}

      {/* Clients Table */}
      <div className="rounded-2xl shadow-md p-5 bg-white text-gray-800 flex-1">
        <h3 className="text-lg font-semibold mb-4">All Clients</h3>
        {isInitialLoading ? (
          <div className="mt-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="py-2">
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 1, width: "100%" }}
                />
              </div>
            ))}
          </div>
        ) : (
          <Table
            columns={clientTableColumns}
            dataSource={clients}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            rowClassName={() => "cursor-pointer hover:bg-gray-50 transition-colors"}
            onRow={(record: Client) => ({
              onClick: () => navigate(`/billing/clients/${record.id}`),
            })}
            loading={loadingClients}
            locale={{
              emptyText: fetchClientsError ? (
                "Unable to load clients"
              ) : (
                <div className="py-10 flex flex-col items-center justify-center gap-3 text-center">
                  <div className="text-gray-500 text-sm">
                    You don't have any clients yet
                  </div>
                  <button
                    type="button"
                    className="btn btn-gray"
                    onClick={handleOpenCreateModal}
                    disabled={loadingClients}
                  >
                    <Plus size={16} />
                    <span>Create client</span>
                  </button>
                </div>
              ),
            }}
          />
        )}
      </div>

      {/* Create client form modal */}
      <ClientFormModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        editingClient={null}
        onClientSaved={refetchClients}
      />
    </div>
  );
}
